/**
 * The words and the choices behind the Rooms list's Now band and Quick lives strip, and a quick live's own page: who
 * is live, what is up next, which lives are quick lives, how long one ran and where its replay is. Pure, so the list,
 * the page and the tests read the same rule.
 */
import { labelParts } from "../../../shared/live-time";
import type { WebinarLive, WebinarLiveGlance, WebinarSummary } from "../../../shared/types";
import { dayWord, liveWhen } from "./roomWords";

const n = (v: number) => v.toLocaleString("en-US");

/** A circle's call lives in its circle: it is never a room and never in the strip. */
export const isCall = (w: Pick<WebinarSummary, "circle_id">): boolean => !!w.circle_id;

/** The rooms of "Your rooms": every room that is not a quick live and not a circle's call. */
export function roomCardsOf(list: readonly WebinarSummary[]): WebinarSummary[] {
  return list.filter((w) => !w.quick_live && !isCall(w));
}

/** The quick lives of the strip, newest first. */
export function quickLivesOf(list: readonly WebinarSummary[]): WebinarSummary[] {
  return list
    .filter((w) => w.quick_live && !isCall(w) && w.live)
    .toSorted((a, b) => Date.parse(b.live?.startsAt ?? "") - Date.parse(a.live?.startsAt ?? ""));
}

/** What is on air now (any live, a circle's call included) and the next live still to come. */
export function nowBandOf(list: readonly WebinarSummary[], nowMs: number): { live: WebinarSummary[]; next: WebinarSummary | null } {
  const live = list.filter((w) => w.status === "published" && w.live?.state === "live");
  const next =
    list
      .filter((w) => !w.quick_live && !isCall(w) && w.status === "published" && w.live?.state === "scheduled" && Date.parse(w.live.startsAt) >= nowMs)
      .toSorted((a, b) => Date.parse(a.live?.startsAt ?? "") - Date.parse(b.live?.startsAt ?? ""))[0] ?? null;
  return { live, next };
}

/** "in 12 min", "in 3 h 20 min", "in 4 days": how long until a moment. */
export function untilWords(iso: string, nowMs: number): string {
  const mins = Math.max(0, Math.round((Date.parse(iso) - nowMs) / 60000));
  if (mins < 60) return `in ${mins} min`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `in ${h} h ${mins % 60} min` : `in ${Math.round(h / 24)} days`;
}

/** "Today, 4:02 PM", "Yesterday, 6:05 PM", else "Thu 22 Oct, 11:30 AM". */
export function callWhen(iso: string, tz: string, nowMs: number): string {
  const day = dayWord(iso, tz, nowMs);
  const time = labelParts(iso, tz, "short")?.time ?? "";
  if (day === "today") return `Today, ${time}`;
  if (day === "yesterday") return `Yesterday, ${time}`;
  return liveWhen(iso, tz);
}

/** How long a live has run: "12 min so far" while on air, "14 min" once ended; empty before it began. */
export function ranWords(live: Pick<WebinarLiveGlance, "state" | "wentLiveAt" | "endedAt">, nowMs: number): string {
  if (live.wentLiveAt == null) return "";
  const onAir = live.state === "live";
  const end = onAir ? Math.floor(nowMs / 1000) : live.endedAt;
  if (end == null) return "";
  const mins = Math.max(1, Math.round((end - live.wentLiveAt) / 60));
  return onAir ? `${n(mins)} min so far` : `${n(mins)} min`;
}

/** The chip on a quick live's tile, from where its replay has got to. */
export function tileStatus(live: Pick<WebinarLiveGlance, "state" | "replay" | "replayPlays">): { text: string; tone: "live" | "" | "soft" | "off" } {
  if (live.state === "live") return { text: "Live now", tone: "live" };
  if (live.replayPlays) return { text: "Replay ready", tone: "" };
  if (live.replay === "preparing") return { text: "Replay in a few minutes", tone: "soft" };
  return { text: "Not recorded", tone: "off" };
}

/** "3 rooms and 4 quick lives, 1,540 people signed up", in as many parts as there are. */
export function roomsAndLivesLine(rooms: readonly WebinarSummary[], quick: number): string {
  const people = rooms.reduce((sum, w) => sum + w.registered, 0);
  const parts = [
    rooms.length > 0 || quick === 0 ? `${n(rooms.length)} ${rooms.length === 1 ? "room" : "rooms"}` : "",
    quick > 0 ? `${n(quick)} quick ${quick === 1 ? "live" : "lives"}` : "",
  ].filter(Boolean);
  const head = parts.join(" and ");
  return people ? `${head}, ${n(people)} ${people === 1 ? "person" : "people"} signed up` : head;
}

/** The Now band's line under a live: who is in the room with the host. */
export function inRoomLine(came: number): string {
  return came > 0 ? `${n(came)} in the room with you` : "No one in the room yet";
}

/** What a quick live's page says beside the picture: how it ran and where its replay is, from the page's own read. */
export function pageReplay(live: Pick<WebinarLive, "state" | "replay">): "none" | "building" | "ready" {
  if (live.replay.playable) return "ready";
  return live.state === "ended" && live.replay.recorded ? "building" : "none";
}

/** The state of the page's follow-up: the replay email waits, went, or was held because email is not set up. */
export function replayMailLine(
  replay: "none" | "building" | "ready",
  mail: { at: number; held: boolean } | null,
  came: number,
): { kind: "sent" | "held" | "waiting" | "none"; text: string } {
  const who = `the ${n(came)} who came`;
  if (mail && mail.held) return { kind: "held", text: "Your email is not set up yet, so the replay was not emailed." };
  if (mail) return { kind: "sent", text: `Replay sent to ${who}` };
  if (replay === "none") return { kind: "none", text: "This call was not recorded, so there is no replay to send." };
  return { kind: "waiting", text: "We send the replay by email the moment it is ready, to everyone who came." };
}

/** One person's second line on the page: how long they stayed, or that they stayed to the end. */
export function stayedWords(p: { pct: number; stay: string }, live: boolean): string {
  if (live) return `${p.stay.split(" ")[0]} min so far`;
  return p.pct >= 100 ? "Stayed to the end" : `Stayed ${p.stay}`;
}
